<script setup lang="ts">import { ref as _ref } from 'vue';

import moment from "moment";
import { computed } from "vue";

import { $can } from "@/services/UtilitiesService";
import { useNotesStore } from "@/stores/notes-store";

import StringTooltip from "@/components/Tooltip/StringTooltip.vue";
import ConfirmPopover from "@/components/ConfirmPopover.vue";

import { ModuleStatus } from "@/models/ModuleStatus";

import type { Metadata } from "@/models/Metadata";

const MAX_NOTE_LENGTH = 4000;

withDefaults(defineProps<{ item: Metadata }>(), {  })
let noteText: string = _ref("");

const isLoading = computed(() => {
  return useNotesStore().moduleStatus !== ModuleStatus.Ready;
});

const notes = computed(() => {
  return (useNotesStore().notesObject[__props.item.original] || [])
    .map((n) => ({
      ...n,
      addedDate: moment.utc(n.added).local().format("YYYY-MM-DD HH:mm"),
    }))
    .sort((a, b) => new Date(b.added).getTime() - new Date(a.added).getTime());
});

async function add(): Promise<void> {
  if (isLoading.value || !noteText.value.trim()) {
    return;
  }
  await useNotesStore().saveNote({
    fileReference: __props.item.original,
    noteText: noteText.value,
  });
  noteText.value = "";
}

async function deleteNote(externalReference: string): Promise<void> {
  if (isLoading.value) {
    return;
  }
  await useNotesStore().deleteNote({
    fileReference: __props.item.original,
    externalReference,
  });
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function refresh(): Promise<void> {
  if (isLoading.value) {
    return;
  }
  await useNotesStore().loadForFile({ fileReference: __props.item.original });
}
</script>
<template>
  <div
    class="notes-wrapper tw-mx-1 tw-mt-1 tw-flex tw-flex-col tw-bg-gray-100 tw-p-1"
    :class="{ 'was-validated': noteText.length >= 4000 }"
  >
    <template v-if="$can('annotate')">
      <label for="newNote" class="tw-text-base tw-font-medium tw-underline tw-underline-offset-2">New Note</label>
      <textarea
        id="newNote"
        v-model="noteText"
        class="tw-rounded-sm tw-border tw-border-gray-400 tw-p-1"
        :maxlength="MAX_NOTE_LENGTH"
      />
      <label for="newNote" class="invalid-feedback">
        <strong>Notes are limited to {{ MAX_NOTE_LENGTH }} characters</strong>
      </label>
      <div class="tw-mb-2 tw-mt-1 tw-flex tw-justify-between">
        <button class="tw-btn-md-gray tw-b-r" @click="add()">Add Note</button>
        <i v-if="isLoading" class="icon fas fa-spin fa-spinner tw-m-2 tw-inline-block"></i>
      </div>
    </template>
    <div class="notes tw-mt-2">
      <h6 class="tw-text-base tw-font-medium tw-underline tw-underline-offset-2">Saved Notes</h6>
      <div
        v-for="(note, index) in notes"
        :key="note.externalReference"
        class="tw-my-1 tw-flex tw-items-center tw-justify-between tw-border tw-border-gray-900 tw-p-1"
      >
        <div tabindex="0" class="tw-flex-grow tw-overflow-auto">
          <span class="tw-sr-only">Note {{ index + 1 }}.</span>
          <p class="note tw-mb-2">{{ note.noteText }}</p>
          <p class="tw-m-0">
            Created by <strong>{{ note.userName }}</strong> at
            <strong>{{ note.addedDate }}</strong>
          </p>
        </div>
        <div v-if="$can('annotate')">
          <ConfirmPopover confirm-text="Delete" @confirmed="() => deleteNote(note.externalReference)">
            <template #trigger>
              <StringTooltip item="Delete Note" #="{ item }">
                <button type="button" class="tw-size-7 tw-rounded-full hover:tw-bg-gray-400" :aria-label="item">
                  <i aria-hidden="true" class="icon fas fa-trash-alt" />
                </button>
              </StringTooltip>
            </template>
            <template #question>Are you sure you want to delete this note?</template>
          </ConfirmPopover>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.notes {
  overflow: auto;
  flex-grow: 1;
  .note {
    white-space: pre-wrap;
    width: calc(100% - 10px);
  }
}
</style>
